import { DataTable, type ColumnDef } from '@/core/ui/components/DataTable';
import { useServices } from '@/app/providers/ServiceContext';
import type { AccessDoor } from '@yawi/types';
import { Camera, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useAccessDoors } from '../hooks/useAccessDoors';
import { AutoMapResultModal } from './AutoMapResultModal';
import { EditDoorCameraModal } from './EditDoorCameraModal';

interface AccessDoorsTabProps {
  tenantId: string;
}

function OriginBadge({ door }: { door: AccessDoor }) {
  if (!door.primaryCameraIndexCode) {
    return <span className="text-theme-secondary text-xs">—</span>;
  }
  return door.cameraMappingOverridden ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
      Manual
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      Auto
    </span>
  );
}

const columns: ColumnDef<AccessDoor>[] = [
  {
    id: 'name',
    header: 'Puerta',
    cell: (door) => <span className="font-medium text-theme">{door.name}</span>,
  },
  {
    id: 'controller',
    header: 'Controlador',
    cell: (door) => (
      <span className="text-theme-secondary text-xs font-mono truncate max-w-[160px] block" title={door.externalDeviceId}>
        {door.externalDeviceId || '—'}
      </span>
    ),
  },
  {
    id: 'camera',
    header: 'Cámara',
    cell: (door) => (
      <span className="text-sm text-theme">
        {door.primaryCameraIndexCode ?? <span className="text-theme-secondary italic">Sin cámara</span>}
      </span>
    ),
  },
  {
    id: 'origin',
    header: 'Origen',
    cell: (door) => <OriginBadge door={door} />,
  },
];

export function AccessDoorsTab({ tenantId }: AccessDoorsTabProps) {
  const { accessDoorRepository } = useServices();
  const { doors, total, page, pageSize, search, loading, setPage, setSearch, refresh } = useAccessDoors(tenantId);

  const [autoMapOpen, setAutoMapOpen] = useState(false);
  const [editDoor, setEditDoor] = useState<AccessDoor | null>(null);

  const meta = {
    page,
    limit: pageSize,
    total,
    totalPages: Math.ceil(total / pageSize),
    hasNextPage: page * pageSize < total,
    hasPreviousPage: page > 1,
  };

  return (
    <div>
      <DataTable<AccessDoor>
        data={doors}
        columns={columns}
        keyExtractor={(d) => d.id}
        meta={total > 0 ? meta : null}
        onPageChange={setPage}
        title="Puertas y Cámaras"
        searchPlaceholder="Buscar por nombre de puerta…"
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        loading={loading}
        emptyMessage="No hay puertas configuradas para este tenant"
        emptyIcon={<Camera className="w-10 h-10 text-theme-secondary opacity-40" />}
        actions={
          <button
            type="button"
            onClick={() => setAutoMapOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            Auto-mapear cámaras
          </button>
        }
        rowActions={(door) => (
          <button
            type="button"
            onClick={() => setEditDoor(door)}
            className="text-xs text-indigo-500 hover:text-indigo-700 transition-colors"
          >
            Editar cámara
          </button>
        )}
      />

      <AutoMapResultModal
        tenantId={tenantId}
        isOpen={autoMapOpen}
        onClose={() => setAutoMapOpen(false)}
        onConfirm={() => { setAutoMapOpen(false); refresh(); }}
        onAutoMap={(tid) => accessDoorRepository.autoMapCameras(tid)}
      />

      <EditDoorCameraModal
        door={editDoor}
        tenantId={tenantId}
        isOpen={!!editDoor}
        onClose={() => setEditDoor(null)}
        onSaved={refresh}
      />
    </div>
  );
}
